import { Routes, Route, Navigate } from 'react-router-dom';

import AdminDashboard from '../components/Admin/AdminDashboard';
import AdminLayout from '../components/Admin/AdminLayout';
import AdminLogin from '../components/Admin/AdminLogin';
import BlogPostEditor from '../components/Admin/BlogPostEditor';
import ContentEditor from '../components/Admin/ContentEditor';
import ContentSelector from '../components/Admin/ContentSelector';
import ImageManager from '../components/Admin/ImageManager';
import MenuEditor from '../components/Admin/MenuEditor';
import ProtectedRoute from '../components/Admin/ProtectedRoute';
import SiteSettingsEditor from '../components/Admin/SiteSettingsEditor';
import { useAdmin } from '../context/AdminContext';

import styles from './AdminPage.module.css';

/**
 * AdminPage Component
 *
 * Main routing container for the entire admin panel. Handles all admin-related
 * routes and implements route-level authentication protection.
 *
 * ## Route Structure
 *
 * This component defines the following routes:
 *
 * ### Public Routes
 * - **`/admin`** - Login page
 *   - Displays login form for unauthenticated users
 *   - Redirects authenticated users to dashboard
 *   - Entry point for admin authentication
 *
 * ### Protected Routes (Require Authentication)
 * All protected routes are wrapped with ProtectedRoute and AdminLayout:
 *
 * - **`/admin/dashboard`** - Admin Dashboard
 *   - Shows site statistics (images, pages, categories count)
 *   - Quick action links to all admin sections
 *   - Primary landing page after login
 *
 * - **`/admin/menu`** - Menu Editor
 *   - Manage circular menu items
 *   - Add/edit/delete/reorder menu items
 *   - Configure menu item properties (label, slug, order, enabled status)
 *
 * - **`/admin/content/:slug`** - Content Editor
 *   - Dynamic route with page slug parameter
 *   - Edit page content based on page type (text-cloud, icon-ring, gallery, shop)
 *   - Content structure varies by page type
 *
 * - **`/admin/images`** - Image Manager
 *   - View all uploaded images
 *   - Filter images by category
 *   - Edit image metadata (alt text, category, order)
 *   - Delete images
 *   - Integrated with image uploader
 *
 * - **`/admin/settings`** - Site Settings
 *   - Manage artist photo and site-wide settings
 *   - Configure application preferences
 *
 * - **`/admin/*`** (Catch-all) - Redirects to `/admin`
 *   - Handles invalid admin routes
 *   - Prevents 404 errors within admin section
 *
 * ## Authentication Flow
 *
 * ```
 * User visits /admin/*
 *   ↓
 * Check isInitializing (AdminContext)
 *   ↓
 * If initializing: Show "Loading..."
 *   ↓
 * If initialized:
 *   ├── Public route (/admin): Show login or redirect to dashboard
 *   └── Protected route:
 *       ├── Authenticated: Render AdminLayout + Component
 *       └── Not authenticated: ProtectedRoute redirects to /admin
 * ```
 *
 * ## Route Protection Strategy
 *
 * - **ProtectedRoute Component**: Wraps all authenticated routes
 *   - Checks `isAuthenticated` from AdminContext
 *   - Redirects to `/admin` if not authenticated
 *   - Uses `replace` navigation to prevent back-button issues
 *
 * - **AdminLayout Component**: Provides consistent admin UI
 *   - Sidebar navigation with active route highlighting
 *   - Logout button in header
 *   - Responsive mobile navigation
 *   - Wraps all protected route content
 *
 * - **Login Route Logic**: Conditional rendering
 *   - If authenticated: `<Navigate to="/admin/dashboard" replace />`
 *   - If not authenticated: `<AdminLogin />` form
 *
 * ## Loading State Management
 *
 * During initial authentication check (`isInitializing`):
 * - Prevents flash of login page for authenticated users
 * - Shows loading indicator while verifying stored token
 * - Ensures smooth user experience on page refresh
 *
 * ## TypeScript Type Safety
 *
 * - All route elements properly typed with JSX.Element
 * - Component imports use explicit types
 * - React Router hooks fully typed
 *
 * @returns {JSX.Element} Admin panel routing structure
 */
const AdminPage = (): JSX.Element => {
  // Get authentication state from AdminContext
  // isAuthenticated: true if user has valid JWT token
  // isInitializing: true during initial token verification on mount
  const { isAuthenticated, isInitializing } = useAdmin();

  // Show loading while checking initial authentication
  // This prevents flash of login page for already-authenticated users
  // who refresh the page or directly navigate to admin routes
  if (isInitializing) {
    return (
      <div className={styles.adminPage}>
        <div className={styles.container}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/*
        PUBLIC ROUTE: Login Page (/admin)
        - Shows AdminLogin form for unauthenticated users
        - Redirects authenticated users to dashboard (prevents seeing login again)
        - Uses 'replace' to avoid adding /admin to browser history
      */}
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/admin/dashboard" replace /> : <AdminLogin />}
      />

      {/*
        PROTECTED ROUTES
        All protected routes follow this pattern:
        1. ProtectedRoute wrapper - checks authentication, redirects if not authenticated
        2. AdminLayout wrapper - provides sidebar navigation and consistent UI
        3. Component - the actual admin page content

        This composition ensures:
        - Authentication is verified before rendering
        - All admin pages have consistent layout
        - Logout button available on all pages
        - Navigation sidebar shows active route
      */}

      {/* PROTECTED: Dashboard - Main admin landing page */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* PROTECTED: Menu Editor - Manage circular menu items */}
      <Route
        path="/menu"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <MenuEditor />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* PROTECTED: Content Selector - Choose which page to edit */}
      <Route
        path="/content"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ContentSelector />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* PROTECTED: Content Editor - Edit page content by slug (dynamic route) */}
      <Route
        path="/content/:slug"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ContentEditor />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* PROTECTED: Image Manager - Upload and manage images */}
      <Route
        path="/images"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ImageManager />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* PROTECTED: Site Settings - Manage artist photo and site-wide settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <SiteSettingsEditor />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* PROTECTED: Blog Post Editor - Create/edit individual blog posts */}
      <Route
        path="/blog/:slug/:postId"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <BlogPostEditor />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/*
        CATCH-ALL ROUTE
        Redirects any unmatched /admin/* routes to /admin (login page)
        - Prevents 404 errors within admin section
        - Uses 'replace' to avoid adding invalid route to browser history
      */}
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
};

export default AdminPage;
